/**
 * Loan migration paths — maps client legacy data to the correct upload templates.
 *
 * DB objects created by running-loans import:
 *   IndividualLoan → IndividualLoanAssess → LoanApproval → LoanIssue → LoanScheduleDate
 *   → LoanRepayment (synthetic opening rows) → Transaction / GeneralJournal
 */

export const LOAN_MIGRATION_PATHS = [
  {
    id: 'snapshot',
    title: 'Path A — Balance snapshot (most common)',
    recommended: true,
    when: 'You only have outstanding balances as at cutover date (loan paid part-way, no need for each past payment).',
    steps: [
      'Import customer accounts first.',
      'Use Running Loans with Amount = original loan, Principle balance = still owed (principal), Interest balance = still owed (interest).',
      'Set Onboarding date = your cutover / go-live date.',
      'Optional: import Loan schedules with exact installment dates from your old system.',
      'Skip the Loan repayments wizard step — balances are already applied.',
    ],
    avoid: 'Do not also import loan repayments for the same historical payments — that double-counts.',
  },
  {
    id: 'full_history',
    title: 'Path B — Full repayment history',
    when: 'You need every historical payment on the loan statement (dates, journals, cash account).',
    steps: [
      'Import customer accounts first.',
      'Running Loans: Amount = original loan, Principle balance = 0, Interest balance = 0 (full loan issued).',
      'Loan repayments: one row per historical payment. LoanId must match LoanNo from step 2.',
      'Use receiver_account = cash/bank GL (e.g. CASH A/C).',
    ],
    avoid: 'Do not set Principle/Interest balance on running loans AND import repayments — pick one approach.',
  },
  {
    id: 'fix_balances',
    title: 'Path C — Fix balances after import',
    when: 'Loans were already imported but principle/interest balances are wrong.',
    steps: [
      'Use Loan opening balances upload (account ID in MemberID column, LoanID = LoanNo, balances, opening date).',
      'This rewrites opening repayment rows for that loan — use carefully.',
    ],
    avoid: 'Not for first-time import — prefer Path A.',
  },
];

export const LOAN_TEMPLATE_FIELD_GUIDE = [
  { column: 'Date', mapsTo: 'LoanIssue.date + IndividualLoan.date', meaning: 'Original loan issue date from old system.' },
  { column: 'Amount', mapsTo: 'LoanIssue.approved_amount', meaning: 'Original approved / disbursed amount (not remaining balance).' },
  { column: 'Period', mapsTo: 'LoanIssue.loan_period', meaning: 'Total loan term in months/weeks per loan type schedule.' },
  { column: 'Loan type', mapsTo: 'IndividualLoan.loan_type', meaning: 'Must match a LoanType name (e.g. BUSINESS LOAN).' },
  { column: 'Account no', mapsTo: 'Member.AccountNumber', meaning: 'Customer account must exist before import.' },
  { column: 'Principle balance', mapsTo: 'LoanRepayment.principle_balance (synthetic)', meaning: 'Remaining principal owed TODAY. System computes principle_paid = Amount − this.' },
  { column: 'Interest balance', mapsTo: 'LoanRepayment.interest_balance (synthetic)', meaning: 'Remaining interest owed TODAY. System computes interest_paid from schedule total − this.' },
  { column: 'Fine balance', mapsTo: 'Loan_ManualFine + member loan_fine', meaning: 'Outstanding fines as at onboarding date.' },
  { column: 'LoanNo', mapsTo: 'LoanIssue.loan_number', meaning: 'Your legacy loan reference. Used by repayments import (LoanId column).' },
  { column: 'Onboarding date', mapsTo: 'Opening balance transaction date', meaning: 'Cutover date for principle/interest/fine opening entries.' },
];

export const LOAN_SCHEDULE_FIELD_GUIDE = [
  { column: 'LoanNo', mapsTo: 'LoanIssue.loan_number', meaning: 'Must match running loans import.' },
  { column: 'Repayment number', mapsTo: 'LoanScheduleDate.repayment_number', meaning: 'Installment sequence: 1, 2, 3…' },
  { column: 'Installment date', mapsTo: 'LoanScheduleDate.date', meaning: 'Exact due date from your old system (DD/MM/YYYY).' },
  { column: 'Principle / Interest', mapsTo: 'LoanScheduleDate.principle / .interest', meaning: 'Scheduled amounts for that installment.' },
  { column: 'Balance', mapsTo: 'LoanScheduleDate.balance', meaning: 'Optional — remaining principle after this installment.' },
];

export const LOAN_REPAYMENT_FIELD_GUIDE = [
  { column: 'LoanId', mapsTo: 'LoanIssue.loan_number', meaning: 'NOT the database ID — must match LoanNo from running loans.' },
  { column: 'Principle / Interest / Fine', mapsTo: 'LoanRepayment + GL + member subaccounts', meaning: 'Each payment creates journals. Run organize_loanTracker after bulk import.' },
];
